import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Users, BookOpen, Pill, CreditCard, MessageSquare, Activity, Shield, Crown } from "lucide-react";
import AdminCMS from "../components/AdminCMS.jsx";
import AdminFeedback from "../components/AdminFeedback.jsx";
import AdminReviewQueue from "../components/AdminReviewQueue.jsx";
import WebhookFailures from "../components/WebhookFailures.jsx";

const statMeta = [
  { key: "users", label: "Total users", icon: Users, color: "bg-[#EFF4FF] text-[#111827]" },
  { key: "active_subs", label: "Active subs", icon: CreditCard, color: "bg-emerald-50 text-emerald-700" },
  { key: "protocols", label: "Protocols", icon: BookOpen, color: "bg-blue-50 text-blue-700" },
  { key: "drugs", label: "Drugs", icon: Pill, color: "bg-amber-50 text-amber-700" },
  { key: "payments_success", label: "Payments", icon: Activity, color: "bg-rose-50 text-rose-700" },
  { key: "chat_sessions", label: "AI sessions", icon: MessageSquare, color: "bg-violet-50 text-violet-700" },
];

export default function Admin() {
  const [stats, setStats] = useState({});
  const [users, setUsers] = useState([]);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
  }, []);

  const [editingUser, setEditingUser] = useState(null);
  const [subForm, setSubForm] = useState({ plan_id: "plan-monthly", status: "active", expires_in_days: 30 });
  const [plans, setPlans] = useState([]);
  useEffect(() => { api.get("/plans").then((r) => setPlans(r.data)).catch(() => {}); }, []);

  const reloadUsers = () => api.get("/admin/users").then((r) => setUsers(r.data));

  const openEdit = (u) => {
    setEditingUser(u);
    setSubForm({
      plan_id: u.subscription_plan || "plan-monthly",
      status: u.subscription_status === "free" ? "active" : u.subscription_status,
      expires_in_days: 30,
    });
  };

  const saveSubscription = async () => {
    try {
      await api.patch(`/admin/users/${editingUser.id}/subscription`, subForm);
      toast.success(`${editingUser.email} updated to ${subForm.status} / ${subForm.plan_id}`);
      setEditingUser(null);
      reloadUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const toggleAdmin = async (u) => {
    const newRole = u.role === "admin" ? "clinician" : "admin";
    if (!window.confirm(`${newRole === "admin" ? "Promote" : "Demote"} ${u.email} to ${newRole}?`)) return;
    try {
      await api.patch(`/admin/users/${u.id}/role`, { role: newRole });
      toast.success(`${u.email} → ${newRole}`);
      reloadUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Role update failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-page">
      <div>
        <div className="text-xs tracking-widest uppercase text-amber-700 font-semibold">Admin · Internal</div>
        <h1 className="font-heading text-3xl font-bold text-[#111827] mt-1">Operations dashboard</h1>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {statMeta.map((s) => (
          <Card key={s.key} className="border-slate-200">
            <CardContent className="p-4">
              <div className={`w-9 h-9 rounded-md flex items-center justify-center mb-2 ${s.color}`}>
                <s.icon className="w-4 h-4" />
              </div>
              <div className="font-heading text-2xl font-extrabold text-[#111827]">{stats[s.key] ?? 0}</div>
              <div className="text-xs text-slate-500">{s.label}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-0">
          <div className="p-4 border-b border-slate-200">
            <h3 className="font-heading font-semibold">Users</h3>
          </div>
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>HPCSA</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Subscription</TableHead>
                <TableHead>Joined</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.slice(0, 100).map((u, i) => (
                <TableRow key={u.id} className={i % 2 ? "bg-slate-50/50" : ""}>
                  <TableCell className="font-medium">{u.full_name}</TableCell>
                  <TableCell className="text-sm text-slate-600">{u.email}</TableCell>
                  <TableCell className="text-xs text-slate-600 font-mono">{u.hpcsa_number || "—"}</TableCell>
                  <TableCell>
                    <Badge className={u.role === "admin" ? "bg-amber-100 text-amber-900 border-0" : "bg-slate-100 text-slate-700 border-0"}>
                      {u.role}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge className={
                      u.subscription_status === "active" ? "bg-emerald-100 text-emerald-800 border-0" :
                      u.subscription_status === "trial" ? "bg-amber-100 text-amber-800 border-0" :
                      "bg-slate-100 text-slate-700 border-0"
                    }>{u.subscription_status}</Badge>
                    <div className="text-[10px] text-slate-500 mt-1">{u.subscription_plan || "—"}</div>
                  </TableCell>
                  <TableCell className="text-sm text-slate-600">{new Date(u.created_at).toLocaleDateString("en-ZA")}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button size="sm" variant="outline" onClick={() => openEdit(u)} data-testid={`upgrade-${u.id}`}>
                        <Crown className="w-3.5 h-3.5 mr-1" /> Plan
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => toggleAdmin(u)} data-testid={`role-${u.id}`}>
                        <Shield className="w-3.5 h-3.5 mr-1" /> {u.role === "admin" ? "Demote" : "Admin"}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Content management (CMS) */}
      <AdminCMS />

      {/* Clinician content review queue */}
      <AdminReviewQueue />

      {/* Payment webhook (Payfast ITN) failures */}
      <WebhookFailures />

      {/* Public feedback moderation */}
      <AdminFeedback />

      <Dialog open={!!editingUser} onOpenChange={(o) => !o && setEditingUser(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Update subscription</DialogTitle>
          </DialogHeader>
          {editingUser && (
            <div className="space-y-4">
              <div className="text-sm text-slate-600">{editingUser.full_name} · {editingUser.email}</div>
              <div>
                <Label>Plan</Label>
                <Select value={subForm.plan_id} onValueChange={(v) => setSubForm({ ...subForm, plan_id: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="sub-plan-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {plans.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} — R{p.price_zar}/{p.interval}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={subForm.status} onValueChange={(v) => setSubForm({ ...subForm, status: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="sub-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["active", "trial", "expired", "free"].map((s) => <SelectItem key={s} value={s}>{s === "trial" ? "trial (7 days)" : s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Days until expiry</Label>
                <Input type="number" value={subForm.expires_in_days} onChange={(e) => setSubForm({ ...subForm, expires_in_days: parseInt(e.target.value) || 30 })} className="mt-1.5" data-testid="sub-days" disabled={subForm.status !== "active"} />
                <p className="text-xs text-slate-500 mt-1">Used only when status = active (30 = month, 365 = year, 3650 = lifetime). A <strong>trial</strong> is always 7 days.</p>
              </div>
              <p className="text-xs text-slate-500 flex items-start gap-1.5 rounded-md bg-slate-50 border border-slate-200 p-2.5" data-testid="upgrade-email-note">
                <Crown className="w-3.5 h-3.5 text-amber-600 flex-shrink-0 mt-0.5" />
                Setting status to <strong>&nbsp;trial&nbsp;</strong> or <strong>&nbsp;active&nbsp;</strong> emails the user to let them know their access was upgraded.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingUser(null)}>Cancel</Button>
            <Button onClick={saveSubscription} className="bg-[#0B5FFF] hover:bg-[#0A54E0]" data-testid="save-subscription">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
